/**
 * Governance Pulse — 11 questions across 3 themes (UI copy). Each option uses
 * `category` matching Board Chemistry dimensions for POST body parity:
 * decision-making | collaboration | sustainability | communication
 */
const C = {
  DM: "decision-making",
  CO: "collaboration",
  SU: "sustainability",
  CM: "communication",
};

export const GOVERNANCE_PULSE_QUESTIONS = [
  {
    id: 1,
    title: "Board role vs management",
    scenario:
      "How clearly does the board distinguish its oversight role from management’s execution role in practice?",
    options: [
      { id: "a", text: "Rarely discussed; roles often blur in meetings", value: 2, category: C.CO },
      { id: "b", text: "Generally clear, with occasional overlap on complex topics", value: 3, category: C.CO },
      { id: "c", text: "Clear framing in agendas and decision records", value: 4, category: C.CO },
      { id: "d", text: "Consistently explicit; mandates and decisions are well documented", value: 5, category: C.CO },
    ],
    defaultAnswer: "c",
  },
  {
    id: 2,
    title: "Committee mandates",
    scenario: "How well understood are committee charters and delegated authorities?",
    options: [
      { id: "a", text: "Charters are outdated or not referenced", value: 2, category: C.CO },
      { id: "b", text: "Known to chairs; less clear to full board", value: 3, category: C.CO },
      { id: "c", text: "Reviewed annually and used in meeting design", value: 4, category: C.CO },
      { id: "d", text: "Actively aligned with strategy and risk appetite", value: 5, category: C.CO },
    ],
    defaultAnswer: "c",
  },
  {
    id: 3,
    title: "Information for decisions",
    scenario:
      "Does the board receive the right information, on time, to decide and follow up?",
    options: [
      { id: "a", text: "Often late or too voluminous to use effectively", value: 2, category: C.CM },
      { id: "b", text: "Adequate but uneven across topics", value: 3, category: C.CM },
      { id: "c", text: "Mostly timely with clear decision asks", value: 4, category: C.CM },
      { id: "d", text: "Structured packs with explicit decision and risk sections", value: 5, category: C.CM },
    ],
    defaultAnswer: "c",
  },
  {
    id: 4,
    title: "Stakeholder expectations",
    scenario: "How well are regulatory and stakeholder expectations reflected in board work?",
    options: [
      { id: "a", text: "Reactive; limited linkage to board agendas", value: 2, category: C.CM },
      { id: "b", text: "Periodic updates; not yet embedded in oversight", value: 3, category: C.CM },
      { id: "c", text: "Regular reporting with defined owners", value: 4, category: C.CM },
      { id: "d", text: "Integrated into strategy, risk, and disclosure cycles", value: 5, category: C.CM },
    ],
    defaultAnswer: "c",
  },
  {
    id: 5,
    title: "Decision quality",
    scenario: "How would you rate the quality of board decisions in the last 12 months?",
    options: [
      { id: "a", text: "Inconsistent framing; limited follow-through", value: 2, category: C.DM },
      { id: "b", text: "Sound on major items; weaker on cross-cutting issues", value: 3, category: C.DM },
      { id: "c", text: "Generally sound with clear rationales", value: 4, category: C.DM },
      { id: "d", text: "Strong; decisions are traceable and reviewed", value: 5, category: C.DM },
    ],
    defaultAnswer: "c",
  },
  {
    id: 6,
    title: "Follow-up on decisions",
    scenario: "How reliably does the board track implementation of its decisions?",
    options: [
      { id: "a", text: "Little structured follow-up", value: 2, category: C.DM },
      { id: "b", text: "Ad hoc updates when issues resurface", value: 3, category: C.DM },
      { id: "c", text: "Standing items or dashboards for key decisions", value: 4, category: C.DM },
      { id: "d", text: "Clear owners, dates, and escalation when off-track", value: 5, category: C.DM },
    ],
    defaultAnswer: "c",
  },
  {
    id: 7,
    title: "Risk and compliance linkage",
    scenario: "How well do board decisions connect to risk and compliance priorities?",
    options: [
      { id: "a", text: "Weak linkage; decisions rarely reference risk", value: 2, category: C.DM },
      { id: "b", text: "Present for major risks only", value: 3, category: C.DM },
      { id: "c", text: "Regular alignment with risk register / compliance plan", value: 4, category: C.DM },
      { id: "d", text: "Embedded in decision templates and minutes", value: 5, category: C.DM },
    ],
    defaultAnswer: "c",
  },
  {
    id: 8,
    title: "Meeting effectiveness",
    scenario: "How effective are board meetings at reaching clear outcomes?",
    options: [
      { id: "a", text: "Often inconclusive or dominated by updates", value: 2, category: C.DM },
      { id: "b", text: "Mixed; some meetings drive decisions, others drift", value: 3, category: C.DM },
      { id: "c", text: "Generally focused with time for debate", value: 4, category: C.DM },
      { id: "d", text: "Strong design; decisions and actions are explicit", value: 5, category: C.DM },
    ],
    defaultAnswer: "c",
  },
  {
    id: 9,
    title: "Board calendar and cadence",
    scenario: "How appropriate is the rhythm of board and committee meetings?",
    options: [
      { id: "a", text: "Ad hoc; cadence does not match workload", value: 2, category: C.SU },
      { id: "b", text: "Fixed schedule but often overloaded", value: 3, category: C.SU },
      { id: "c", text: "Balanced with room for deep dives", value: 4, category: C.SU },
      { id: "d", text: "Aligned to strategy cycle and risk events", value: 5, category: C.SU },
    ],
    defaultAnswer: "c",
  },
  {
    id: 10,
    title: "Evaluation and improvement",
    scenario: "How systematically does the board review its own effectiveness?",
    options: [
      { id: "a", text: "Rarely or informally", value: 2, category: C.SU },
      { id: "b", text: "Occasional discussion without a fixed process", value: 3, category: C.SU },
      { id: "c", text: "Periodic evaluation with agreed actions", value: 4, category: C.SU },
      { id: "d", text: "Structured cycle tied to development plan", value: 5, category: C.SU },
    ],
    defaultAnswer: "c",
  },
  {
    id: 11,
    title: "Documentation and audit trail",
    scenario: "How confident are you that board work is documented for audit and disclosure needs?",
    options: [
      { id: "a", text: "Gaps in minutes and decision records", value: 2, category: C.SU },
      { id: "b", text: "Basic records; not always decision-ready", value: 3, category: C.SU },
      { id: "c", text: "Generally complete with clear approvals", value: 4, category: C.SU },
      { id: "d", text: "Strong traceability across decisions and materials", value: 5, category: C.SU },
    ],
    defaultAnswer: "c",
  },
];
